/**
 * Pagina TwoFactorRecoveryPage
 * Il Diario di Bordo - SaaS
 *
 * Pagina per conferma reset/recovery 2FA.
 * URL: /2fa/recovery/:token o /2fa/reset/:token
 * 
 * FIX: Usa endpoint /auth/2fa-recovery/* invece di /auth/2fa/*
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import api from '../../services/api'
import {
  Shield,
  ShieldOff,
  Loader2,
  Check,
  AlertTriangle,
  ArrowLeft,
  Eye,
  EyeOff
} from 'lucide-react'

export default function TwoFactorRecoveryPage() {
  const { token } = useParams()
  const navigate = useNavigate()
  
  const [step, setStep] = useState('loading') // loading, verify, confirm, success, error
  const [tokenData, setTokenData] = useState(null)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Determina se è recovery self-service o reset da admin
  const isRecovery = window.location.pathname.includes('/recovery/')

  useEffect(() => {
    verifyToken()
  }, [token])

  const verifyToken = async () => {
    try {
      // FIX: Usa /auth/2fa-recovery/* invece di /auth/2fa/*
      const endpoint = isRecovery 
        ? `/auth/2fa-recovery/recovery/verify/${token}`
        : `/auth/2fa-recovery/reset/verify/${token}`
        
      const response = await api.get(endpoint)
      setTokenData(response.data.data)
      setStep('verify')
    } catch (err) {
      setError(err.response?.data?.message || 'Token non valido o scaduto')
      setStep('error')
    }
  }

  const handleConfirm = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      // FIX: Usa /auth/2fa-recovery/* invece di /auth/2fa/*
      const endpoint = isRecovery 
        ? `/auth/2fa-recovery/recovery/confirm/${token}`
        : `/auth/2fa-recovery/reset/confirm/${token}`
      
      // Recovery self-service richiede password, reset da admin no
      const body = isRecovery ? { password } : {}
      
      await api.post(endpoint, body)
      setStep('success')
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella conferma')
    } finally {
      setLoading(false)
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Loading
  // ═══════════════════════════════════════════════════════════════════════════

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Verifica in corso...</p>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Error
  // ═══════════════════════════════════════════════════════════════════════════

  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mb-6">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Link non valido
          </h1>
          
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error}
          </p>
          
          <Link to="/login" className="btn-primary inline-flex items-center">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna al login
          </Link>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Success
  // ═══════════════════════════════════════════════════════════════════════════

  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-6">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            2FA Disattivata
          </h1>
          
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            L'autenticazione a due fattori è stata disattivata. 
            Ora puoi accedere solo con email e password.
          </p>
          
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Consiglio:</strong> Per la sicurezza del tuo account, 
              ti consigliamo di riconfigurare la 2FA dopo il login.
            </p>
          </div>
          
          <Link to="/login" className="btn-primary inline-flex items-center">
            Vai al login
          </Link>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Verify / Confirm Form
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 mb-4">
            <ShieldOff className="h-8 w-8 text-orange-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {isRecovery ? 'Recovery 2FA' : 'Reset 2FA'}
          </h1>
          
          <p className="text-gray-600 dark:text-gray-400">
            {isRecovery 
              ? 'Conferma il recovery per disattivare la 2FA'
              : 'Un amministratore ha richiesto il reset della tua 2FA'
            }
          </p>
        </div>

        {/* Token Info */}
        {tokenData && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Account</span>
              <span className="font-medium text-gray-900 dark:text-white">{tokenData.email}</span>
            </div>
            {tokenData.userName && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Nome</span>
                <span className="font-medium text-gray-900 dark:text-white">{tokenData.userName}</span>
              </div>
            )}
          </div>
        )}

        {/* Warning */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>Attenzione:</strong> Questa azione disattiverà completamente 
            l'autenticazione a due fattori. Tutti i metodi configurati 
            (TOTP, Email, Passkey) e i codici di backup saranno rimossi.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleConfirm}>
          
          {/* Password field (solo per recovery self-service) */}
          {isRecovery && tokenData?.requiresPassword && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Conferma la tua password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="Inserisci la password"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Per confermare il recovery, inserisci la password del tuo account.
              </p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col gap-3">
            <button
              type="submit"
              disabled={loading || (isRecovery && tokenData?.requiresPassword && !password)}
              className="btn-danger w-full flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Conferma in corso...
                </>
              ) : (
                <>
                  <ShieldOff className="h-4 w-4 mr-2" />
                  Conferma disattivazione 2FA
                </>
              )}
            </button>
            
            <Link
              to="/login"
              className="btn-secondary w-full flex items-center justify-center"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Annulla
            </Link>
          </div>
        </form>

        {/* Help text */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          Hai problemi?{' '}
          <a href="mailto:support@ildiariodibordo.it" className="text-primary-600 hover:underline">
            Contatta il supporto
          </a>
        </p>
      </div>
    </div>
  )
}
